import React, { useState, useEffect, useCallback } from "react";
import {
  fetchHrLeaveRequests,
  updateHrLeaveStatus,
  fetchHrEmployees,
  fetchHrLeaveTypes,
  updateHrLeaveRequest,
  deleteHrLeaveRequest,
} from "../../../apis/hrApi";
import toast from "react-hot-toast";
import {
  IconSearch,
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconEye,
  IconCheck,
  IconX,
  IconDots,
  IconMoodSmile,
  IconUpload,
  IconDownload,
  IconFilter,
  IconRefresh,
  IconPencil,
  IconTrash,
  IconAlertTriangle,
  IconFileText,
  IconCalendar,
} from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import NewCommonModalCenter from "../../../components/common/NewCommonModalCenter";
import AddLeaveModal from "./AddLeaveModal";
import EntriesSelector from "../../../components/common/EntriesSelector";

const SectionBlock = ({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
}) => (
  <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-slate-200/50 shadow-sm p-4 transition-all duration-300 space-y-4 relative">
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
    <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3 relative z-10">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {title}
      </p>
    </div>
    <div className="relative z-10">
      {children}
    </div>
  </div>
);

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  ({ label, placeholder, type = "text", className = "", error, required, ...rest }, ref) => {
    return (
      <div className={`w-full space-y-1 ${className}`}>
        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            ref={ref}
            type={type}
            placeholder={placeholder}
            {...rest}
            className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
          />
        </div>
        {error && <p className="text-red-500 text-xs">{error}</p>}
      </div>
    );
  }
);
InputField.displayName = "InputField";

const SelectInput = ({
  label,
  value,
  setValue,
  selectOptions = [],
  className = "",
  required,
  disabled,
}: {
  label: string;
  value?: string | number;
  setValue?: (val: string) => void;
  selectOptions?: { value: string | number; text: string }[] | false;
  className?: string;
  required?: boolean;
  disabled?: boolean;
}) => {
  return (
    <div className={`space-y-1 w-full ${className}`}>
      <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative flex items-center w-full">
        <select
          value={value}
          onChange={(e) => setValue && setValue(e.target.value)}
          disabled={disabled}
          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed cursor-pointer"
        >
          <option value="">Select</option>
          {selectOptions &&
            selectOptions.map((opt, i) => (
              <option key={i} value={opt.value}>
                {opt.text}
              </option>
            ))}
        </select>
      </div>
    </div>
  );
};

interface TextareaFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

const TextareaField = ({
  label,
  name,
  placeholder,
  rows = 3,
  value,
  onChange,
  disabled,
  ...rest
}: TextareaFieldProps) => {
  return (
    <div className="w-full space-y-1">
      {label && (
        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
          {label}
        </label>
      )}
      <textarea
        name={name}
        placeholder={placeholder}
        rows={rows}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...rest}
        className="w-full rounded-xl py-2 px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
      />
    </div>
  );
};

interface LeaveDay {
  id: any;
  date: string;
  type: string;
  appliedDate: string;
  status: "Pending" | "Approved" | "Rejected" | "Cancelled";
}

interface LeaveItem {
  id: any;
  name: string;
  dept: string;
  avatar: string;
  leavePeriod: string;
  days: number;
  type: string;
  appliedDate: string;
  processedBy: {
    name: string;
    dept: string;
    avatar: string;
  } | null;
  status: "Pending" | "Approved" | "Rejected" | "View Leaves" | "Cancelled";
  createdAt: string;
  leaveDays?: LeaveDay[];
  raw?: any;
  isSingleDayOfMultiDay?: boolean;
  parentLeaveId?: any;
}

const initialLeavesData: LeaveItem[] = [
  {
    id: 1,
    name: "Yash Koladiya",
    dept: "Human Resources",
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=100",
    leavePeriod: "30-04-2026",
    days: 1,
    type: "Sick",
    appliedDate: "29-04-2026",
    processedBy: null,
    status: "Pending",
    createdAt: "29-04-2026",
  },
  {
    id: 3,
    name: "Ramesh",
    dept: "Quality Control Department",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=100",
    leavePeriod: "10-04-2026",
    days: 1,
    type: "Sick",
    appliedDate: "07-04-2026",
    processedBy: {
      name: "Yash Koladiya",
      dept: "Human Resources",
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=100",
    },
    status: "Rejected",
    createdAt: "07-04-2026",
  },
  {
    id: 4,
    name: "Pal",
    dept: "Software Development",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=100",
    leavePeriod: "08-04-2026",
    days: 1,
    type: "Comp Off",
    appliedDate: "07-04-2026",
    processedBy: null,
    status: "Approved",
    createdAt: "07-04-2026",
  },
  {
    id: 5,
    name: "Pal",
    dept: "Software Development",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=100",
    leavePeriod: "02-04-2026",
    days: 1,
    type: "Comp Off",
    appliedDate: "07-04-2026",
    processedBy: null,
    status: "Approved",
    createdAt: "07-04-2026",
  },
  {
    id: 6,
    name: "Deepesh Patel",
    dept: "Human Resources",
    avatar: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&q=80&w=100",
    leavePeriod: "23-03-2026",
    days: 1,
    type: "Casual",
    appliedDate: "18-03-2026",
    processedBy: {
      name: "Ema",
      dept: "Human Resources",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=100",
    },
    status: "Approved",
    createdAt: "18-03-2026",
  },
  {
    id: 7,
    name: "Abhishek",
    dept: "Human Resources",
    avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80&w=100",
    leavePeriod: "23-03-2026 - 21-03-2026",
    days: 3,
    type: "Comp Off",
    appliedDate: "19-03-2026",
    processedBy: null,
    status: "View Leaves",
    createdAt: "19-03-2026",
    leaveDays: [
      { id: 71, date: "23-03-2026", type: "Comp Off", appliedDate: "20-05-2026", status: "Pending" },
      { id: 72, date: "22-03-2026", type: "Comp Off", appliedDate: "20-05-2026", status: "Pending" },
      { id: 73, date: "21-03-2026", type: "Comp Off", appliedDate: "20-05-2026", status: "Pending" },
    ],
  },
  {
    id: 8,
    name: "Abhishek",
    dept: "Human Resources",
    avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80&w=100",
    leavePeriod: "20-03-2026",
    days: 1,
    type: "Casual",
    appliedDate: "19-03-2026",
    processedBy: null,
    status: "Pending",
    createdAt: "19-03-2026",
  },
  {
    id: 9,
    name: "Deepesh Patel",
    dept: "Human Resources",
    avatar: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&q=80&w=100",
    leavePeriod: "15-03-2026",
    days: 1,
    type: "Casual",
    appliedDate: "12-03-2026",
    processedBy: null,
    status: "Pending",
    createdAt: "12-03-2026",
  },
  {
    id: 10,
    name: "Roshan",
    dept: "IT & Software",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=100",
    leavePeriod: "10-03-2026",
    days: 1,
    type: "Sick",
    appliedDate: "05-03-2026",
    processedBy: null,
    status: "Pending",
    createdAt: "05-03-2026",
  },
];

export default function Leaves() {
  const [leavesData, setLeavesData] = useState<LeaveItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLeaves, setTotalLeaves] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [entriesLimit, setEntriesLimit] = useState(10);
  const [activeDropdownId, setActiveDropdownId] = useState<any | null>(null);

  // Modal / Sidebar State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<LeaveItem | null>(null);
  const [dialogDropdownId, setDialogDropdownId] = useState<number | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<"ADD" | "EDIT" | "VIEW" | "DELETE" | "VIEW_LEAVES" | null>(null);

  // Edit Form State
  const [editDurationType, setEditDurationType] = useState<"Full Day" | "Multiple" | "First Half" | "Second Half">("Full Day");
  const [editSelectedDate, setEditSelectedDate] = useState("");
  const [editFromDate, setEditFromDate] = useState("");
  const [editToDate, setEditToDate] = useState("");
  const [editEmployeeId, setEditEmployeeId] = useState("");
  const [editLeaveTypeId, setEditLeaveTypeId] = useState("");
  const [editStatus, setEditStatus] = useState<"PENDING" | "APPROVED" | "REJECTED">("PENDING");
  const [editReason, setEditReason] = useState("");
  const [employeesList, setEmployeesList] = useState<any[]>([]);
  const [leaveTypesList, setLeaveTypesList] = useState<any[]>([]);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  // Reject Modal State
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectionLeaveId, setRejectionLeaveId] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isRejectSubmitting, setIsRejectSubmitting] = useState(false);

  // Day Actions Modal State
  const [dayModalMode, setDayModalMode] = useState<"VIEW" | "EDIT" | "DELETE" | null>(null);
  const [selectedDayItem, setSelectedDayItem] = useState<any | null>(null);
  const [editDayType, setEditDayType] = useState("");
  const [editDayStatus, setEditDayStatus] = useState<LeaveDay["status"]>("Pending");
  const [editDayReason, setEditDayReason] = useState("");

  // Load employee and leave type options for edit select boxes
  const loadOptions = useCallback(async () => {
    try {
      const empRes = await fetchHrEmployees();
      if (empRes.success && empRes.data?.employees) {
        setEmployeesList(empRes.data.employees);
      }
      const typeRes = await fetchHrLeaveTypes();
      if (typeRes.success) {
        const typesList = typeRes.data?.leaveTypes || typeRes.data || [];
        setLeaveTypesList(typesList);
      }
    } catch (err) {
      console.error("Failed to load options for edit", err);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const loadLeaveRequests = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchHrLeaveRequests(currentPage, entriesLimit);
      if (response.success) {
        const leavesList = response.data?.leaveRequests || response.data?.leaves || response.data?.requests || (Array.isArray(response.data) ? response.data : []);
        const mapped = leavesList.map((item: any) => {
          const id = item.id || item._id;
          const name = item.employee
            ? `${item.employee.firstName || ""} ${item.employee.lastName || ""}`.trim()
            : item.employeeName || item.name || "Unknown";
          const dept = item.employee?.departmentName || item.employee?.department?.branchName || item.departmentName || "-";
          const avatar = item.employee?.profilePic || item.employee?.avatar || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&q=80&w=100";
          
          let leavePeriod = "";
          let days = item.noOfLeaves || item.noOfDays || 1;
          if (item.startDate) {
            const startF = new Date(item.startDate).toLocaleDateString();
            const endF = item.endDate ? new Date(item.endDate).toLocaleDateString() : startF;
            leavePeriod = startF === endF ? startF : `${startF} - ${endF}`;
            
            if (!item.noOfLeaves && !item.noOfDays && item.endDate) {
              const startD = new Date(item.startDate);
              const endD = new Date(item.endDate);
              const diffTime = Math.abs(endD.getTime() - startD.getTime());
              days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            }
          } else {
            leavePeriod = item.leavePeriod || "-";
          }

          const type = item.leaveType?.leaveType || item.leaveType || item.type || "Casual";
          const appliedDate = item.createdAt ? new Date(item.createdAt).toLocaleDateString() : item.appliedDate || "-";
          const createdAt = item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-";
          
          const processedBy = item.processedByUser ? {
            name: `${item.processedByUser.firstName || ""} ${item.processedByUser.lastName || ""}`.trim() || "Admin",
            dept: item.processedByUser.departmentName || "Management",
            avatar: item.processedByUser.profilePic || item.processedByUser.avatar || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=100"
          } : item.processedBy || null;

          let status: LeaveItem["status"] = "Pending";
          if (item.status === "APPROVED" || item.status === "Approved") status = "Approved";
          else if (item.status === "REJECTED" || item.status === "Rejected") status = "Rejected";
          
          const leaveDays: LeaveDay[] = [];
          const backendDates = item.leaveDates || item.dates || item.leaveDays || [];
          if (Array.isArray(backendDates) && backendDates.length > 0) {
            backendDates.forEach((d: any) => {
              const dateVal = d.date || d.leaveDate || d.startDate || d.endDate;
              const dateStr = dateVal ? new Date(dateVal).toLocaleDateString() : "-";
              
              let dayStatus: LeaveDay["status"] = "Pending";
              if (d.status === "APPROVED" || d.status === "Approved") dayStatus = "Approved";
              else if (d.status === "REJECTED" || d.status === "Rejected") dayStatus = "Rejected";
              else if (d.status === "CANCELLED" || d.status === "Cancelled") dayStatus = "Cancelled";

              leaveDays.push({
                id: d.id || d._id || Math.floor(Math.random() * 1000000),
                date: dateStr,
                type: d.type || type,
                appliedDate: appliedDate,
                status: dayStatus
              });
            });
          } else if (days > 1 && item.startDate && item.endDate) {
            let curr = new Date(item.startDate);
            const endD = new Date(item.endDate);
            while (curr <= endD) {
              const dStr = curr.toLocaleDateString();
              leaveDays.push({
                id: Math.floor(Math.random() * 1000000),
                date: dStr,
                type: type,
                appliedDate: appliedDate,
                status: status as any
              });
              curr.setDate(curr.getDate() + 1);
            }
          }

          return {
            id,
            name,
            dept,
            avatar,
            leavePeriod,
            days,
            type,
            appliedDate,
            processedBy,
            status: days > 1 ? "View Leaves" : status,
            createdAt,
            leaveDays: leaveDays.length > 0 ? leaveDays : undefined,
            raw: item
          };
        });
        setLeavesData(mapped);
        setTotalLeaves(response.data?.total || response.data?.totalCount || mapped.length);
      } else {
        setError("Failed to fetch leaves data");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch leaves data");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, entriesLimit]);

  useEffect(() => {
    loadLeaveRequests();
  }, [loadLeaveRequests]);

  const handleSaveNewLeave = () => {
    loadLeaveRequests();
  };

  const handleStatusChange = async (id: any, newStatus: "Pending" | "Approved" | "Rejected") => {
    if (newStatus === "Rejected") {
      setRejectionLeaveId(id);
      setRejectionReason("");
      setIsRejectModalOpen(true);
      return;
    }

    try {
      const apiStatus = newStatus === "Approved" ? "APPROVED" : "PENDING";
      const res = await updateHrLeaveStatus(id, apiStatus);
      if (res.success) {
        toast.success(`Leave request status updated to ${newStatus}`);
        loadLeaveRequests();
      } else {
        toast.error("Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update leave status");
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Please enter a reason for rejection.");
      return;
    }
    setIsRejectSubmitting(true);
    try {
      const res = await updateHrLeaveStatus(rejectionLeaveId, "REJECTED", rejectionReason.trim());
      if (res.success) {
        toast.success("Leave request status updated to Rejected");
        setIsRejectModalOpen(false);
        setRejectionLeaveId(null);
        setRejectionReason("");
        loadLeaveRequests();
      } else {
        toast.error(res.message || "Failed to update status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update leave status");
    } finally {
      setIsRejectSubmitting(false);
    }
  };

  const handleOpenDayActionInSidebar = (day: any, mode: "VIEW" | "EDIT" | "DELETE") => {
    if (!selectedLeave) return;

    // Parse date from DD-MM-YYYY or DD/MM/YYYY format to YYYY-MM-DD for date inputs
    let formattedDate = "";
    if (day.date) {
      const parts = day.date.split("/");
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
      } else {
        const hyphenParts = day.date.split("-");
        if (hyphenParts.length === 3) {
          formattedDate = `${hyphenParts[2]}-${hyphenParts[1].padStart(2, "0")}-${hyphenParts[0].padStart(2, "0")}`;
        }
      }
    }

    const singleDayItem: LeaveItem = {
      id: day.id,
      name: selectedLeave.name,
      dept: selectedLeave.dept,
      avatar: selectedLeave.avatar,
      leavePeriod: day.date,
      days: 1,
      type: day.type || selectedLeave.type,
      appliedDate: day.appliedDate || selectedLeave.appliedDate,
      status: day.status,
      processedBy: selectedLeave.processedBy,
      createdAt: selectedLeave.createdAt,
      raw: {
        id: day.id,
        employeeId: selectedLeave.raw?.employeeId || selectedLeave.raw?.employee?.id || selectedLeave.raw?.employee?._id,
        employee: selectedLeave.raw?.employee,
        leaveTypeId: selectedLeave.raw?.leaveTypeId || selectedLeave.raw?.leaveType?.id || selectedLeave.raw?.leaveType?._id,
        leaveType: { leaveType: day.type || selectedLeave.type },
        leaveMode: "FULL_DAY",
        startDate: formattedDate,
        endDate: formattedDate,
        reason: selectedLeave.raw?.reason || "",
        status: day.status === "Approved" ? "APPROVED" : day.status === "Rejected" ? "REJECTED" : "PENDING"
      },
      isSingleDayOfMultiDay: true,
      parentLeaveId: selectedLeave.id
    };

    // Close the multiple-day list modal
    setIsModalOpen(false);

    // Set edit form values
    setEditEmployeeId(singleDayItem.raw.employeeId || "");
    setEditLeaveTypeId(singleDayItem.raw.leaveTypeId || "");
    setEditStatus(singleDayItem.raw.status || "PENDING");
    setEditReason(singleDayItem.raw.reason || "");
    setEditDurationType("Full Day");
    setEditSelectedDate(formattedDate);
    setEditFromDate("");
    setEditToDate("");

    // Set selected leave and open sidebar
    setSelectedLeave(singleDayItem);
    setSidebarMode(mode);
  };

  const handleOpenModal = (leave: LeaveItem) => {
    setSelectedLeave(leave);
    setSidebarMode("VIEW_LEAVES");
  };

  const handleCloseModal = () => {
    setSelectedLeave(null);
    setSidebarMode(null);
    setDialogDropdownId(null);
  };

  const handleDialogDayStatusChange = (dayId: number, newStatus: "Pending" | "Approved" | "Rejected") => {
    if (!selectedLeave) return;
    const updatedDays = selectedLeave.leaveDays?.map((d) =>
      d.id === dayId ? { ...d, status: newStatus } : d
    ) || [];

    // Determine overall status based on days status
    let overallStatus: LeaveItem["status"] = "Pending";
    if (updatedDays.every((d) => d.status === "Approved")) {
      overallStatus = "Approved";
    } else if (updatedDays.every((d) => d.status === "Rejected")) {
      overallStatus = "Rejected";
    } else {
      overallStatus = "View Leaves";
    }

    setLeavesData((prev) =>
      prev.map((item) =>
        item.id === selectedLeave.id
          ? { ...item, status: overallStatus, leaveDays: updatedDays }
          : item
      )
    );
    setSelectedLeave({ ...selectedLeave, status: overallStatus, leaveDays: updatedDays });
  };

  const handleApproveAll = async () => {
    if (!selectedLeave) return;
    try {
      const res = await updateHrLeaveStatus(selectedLeave.id, "APPROVED");
      if (res.success) {
        toast.success("Leave approved");
        loadLeaveRequests();
        handleCloseModal();
      } else {
        toast.error("Failed to approve leave");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to approve leave");
    }
  };

  const handleRejectAll = async () => {
    if (!selectedLeave) return;
    try {
      const res = await updateHrLeaveStatus(selectedLeave.id, "REJECTED");
      if (res.success) {
        toast.success("Leave rejected");
        loadLeaveRequests();
        handleCloseModal();
      } else {
        toast.error("Failed to reject leave");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to reject leave");
    }
  };

  const handleCancelAll = async () => {
    if (!selectedLeave) return;
    try {
      const res = await updateHrLeaveStatus(selectedLeave.id, "REJECTED");
      if (res.success) {
        toast.success("Leave cancelled");
        loadLeaveRequests();
        handleCloseModal();
      } else {
        toast.error("Failed to cancel leave");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to cancel leave");
    }
  };

  const handleOpenEdit = async (leave: LeaveItem) => {
    setSelectedLeave(leave);
    
    // Set edit form values from raw leave request object
    const raw = leave.raw || {};
    setEditEmployeeId(raw.employeeId || raw.employee?.id || raw.employee?._id || "");
    setEditLeaveTypeId(raw.leaveTypeId || raw.leaveType?.id || raw.leaveType?._id || "");
    setEditStatus(raw.status || "PENDING");
    setEditReason(raw.reason || "");
    
    const mode = raw.leaveMode || "FULL_DAY";
    if (mode === "FIRST_HALF") setEditDurationType("First Half");
    else if (mode === "SECOND_HALF") setEditDurationType("Second Half");
    else if (mode === "MULTIPLE") setEditDurationType("Multiple");
    else setEditDurationType("Full Day");

    const formatDate = (dStr: string) => {
      if (!dStr) return "";
      const d = new Date(dStr);
      if (isNaN(d.getTime())) return "";
      return d.toISOString().split("T")[0];
    };

    if (mode === "MULTIPLE") {
      setEditFromDate(formatDate(raw.startDate));
      setEditToDate(formatDate(raw.endDate));
      setEditSelectedDate("");
    } else {
      setEditSelectedDate(formatDate(raw.startDate));
      setEditFromDate("");
      setEditToDate("");
    }

    setSidebarMode("EDIT");
  };

  const handleSaveEdit = async () => {
    if (!selectedLeave) return;
    if (!editEmployeeId) {
      toast.error("Please select an employee");
      return;
    }
    if (editDurationType === "Multiple" && (!editFromDate || !editToDate)) {
      toast.error("Please select start and end dates");
      return;
    }
    if (editDurationType !== "Multiple" && !editSelectedDate) {
      toast.error("Please select a date");
      return;
    }
    if (!editReason.trim()) {
      toast.error("Please enter a reason for absence");
      return;
    }

    setIsSubmittingEdit(true);
    try {
      if (selectedLeave.isSingleDayOfMultiDay) {
        // Hitting the single-day status API!
        const apiStatus =
          editStatus === "APPROVED"
            ? "APPROVED"
            : editStatus === "REJECTED"
            ? "REJECTED"
            : "PENDING";

        const res = await updateHrLeaveStatus(
          selectedLeave.parentLeaveId,
          apiStatus,
          editStatus === "REJECTED" ? editReason || "Rejected by Admin" : undefined,
          [selectedLeave.id]
        );

        if (res.success) {
          toast.success("Leave day status updated successfully!");
          setSidebarMode(null);
          setSelectedLeave(null);
          loadLeaveRequests();
        } else {
          toast.error(res.message || "Failed to update leave day status");
        }
      } else {
        const payload = {
          employeeId: editEmployeeId,
          leaveTypeId: editLeaveTypeId,
          leaveMode: editDurationType === "Full Day" ? "FULL_DAY" : editDurationType === "First Half" ? "FIRST_HALF" : editDurationType === "Second Half" ? "SECOND_HALF" : "MULTIPLE",
          startDate: editDurationType === "Multiple" ? editFromDate : editSelectedDate,
          endDate: editDurationType === "Multiple" ? editToDate : editSelectedDate,
          reason: editReason,
          status: editStatus,
        };

        const id = selectedLeave.id || selectedLeave.raw?.id || selectedLeave.raw?._id;
        const res = await updateHrLeaveRequest(id, payload);
        if (res.success) {
          toast.success("Leave request updated successfully!");
          setSidebarMode(null);
          setSelectedLeave(null);
          loadLeaveRequests();
        } else {
          toast.error(res.message || "Failed to update leave request");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to update leave request");
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const handleConfirmDeleteLeave = async () => {
    if (!selectedLeave) return;
    try {
      const id = selectedLeave.id || selectedLeave.raw?.id || selectedLeave.raw?._id;
      if (selectedLeave.isSingleDayOfMultiDay) {
        const res = await updateHrLeaveStatus(
          selectedLeave.parentLeaveId,
          "REJECTED",
          "Cancelled/Deleted by Admin",
          [selectedLeave.id]
        );
        if (res.success) {
          toast.success("Leave day deleted/rejected successfully!");
          setSidebarMode(null);
          setSelectedLeave(null);
          loadLeaveRequests();
        } else {
          toast.error(res.message || "Failed to delete leave day");
        }
      } else {
        const res = await deleteHrLeaveRequest(id);
        if (res.success) {
          toast.success("Leave request deleted successfully");
          setSidebarMode(null);
          setSelectedLeave(null);
          loadLeaveRequests();
        } else {
          toast.error(res.message || "Failed to delete leave request");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete leave request");
    }
  };

  // Filter leaves based on search query
  const filteredLeaves = leavesData.filter(
    (item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.dept.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const displayedLeaves = filteredLeaves.slice(0, entriesLimit);

  // Column definitions for ListView
  const columns = [
    {
      label: (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      ),
      render: () => (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      ),
    },
    {
      label: "SR.NO",
      render: (_: any, idx: number) => <span className="font-semibold text-[#212121]">{idx + 1}</span>,
    },
    {
      label: "EMPLOYEE NAME",
      render: (s: any) => (
        <div className="flex items-center gap-3 text-left">
          <img src={s.avatar} className="w-9 h-9 rounded-full object-cover border border-gray-200 flex-shrink-0" alt={s.name} />
          <div>
            <div className="font-semibold text-[13px] text-[#212121]">{s.name}</div>
            <div className="text-[11px] text-gray-400">{s.dept}</div>
          </div>
        </div>
      ),
    },
    {
      label: "LEAVE PERIOD",
      render: (s: any) => (
        <div className="flex items-center justify-center gap-2">
          <span className="text-[13px] text-[#212121] font-semibold">{s.leavePeriod}</span>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${
            s.days === 1
              ? "text-blue-600 bg-blue-50 border-blue-100"
              : "text-orange-600 bg-orange-50 border-orange-100"
          }`}>
            {s.days} {s.days === 1 ? "Day" : "days"}
          </span>
        </div>
      ),
    },
    {
      label: "TYPE",
      render: (s: any) => <span className="text-[13px] text-gray-700 font-medium">{s.type}</span>,
    },
    {
      label: "APPLIED DATE",
      render: (s: any) => <span className="text-[13px] text-gray-700">{s.appliedDate}</span>,
    },
    {
      label: "PROCESSED BY",
      render: (s: any) => s.processedBy ? (
        <div className="flex items-center gap-2 text-left">
          <img src={s.processedBy.avatar} className="w-7 h-7 rounded-full object-cover border border-gray-200 flex-shrink-0" alt={s.processedBy.name} />
          <div>
            <div className="font-semibold text-[12px] text-[#212121]">{s.processedBy.name}</div>
            <div className="text-[10px] text-gray-400">{s.processedBy.dept}</div>
          </div>
        </div>
      ) : (
        <span className="text-gray-300 font-semibold">-</span>
      ),
    },
    {
      label: "STATUS",
      render: (s: any) => {
        const isApproved = s.status === "Approved";
        const isRejected = s.status === "Rejected";
        const isViewLeaves = s.status === "View Leaves";

        if (isViewLeaves) {
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal(s);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 text-[11px] font-bold transition shadow-sm cursor-pointer"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
              <span>View Leaves</span>
              <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          );
        }

        return (
          <div className="inline-block relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveDropdownId(activeDropdownId === s.id ? null : s.id);
              }}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl border text-[11px] font-bold transition shadow-sm cursor-pointer ${
                isApproved
                  ? "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
                  : isRejected
                  ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                  : "border-yellow-200 bg-yellow-50 text-yellow-600 hover:bg-yellow-100"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${
                isApproved ? "bg-green-500" : isRejected ? "bg-red-500" : "bg-yellow-500"
              }`} />
              <span>{s.status}</span>
              <svg className="w-3 h-3 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
            {activeDropdownId === s.id && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setActiveDropdownId(null)} />
                <div className="absolute z-30 mt-1 w-28 bg-white rounded-lg border border-gray-200 shadow-lg py-1 left-1/2 -translate-x-1/2">
                  {(["Pending", "Approved", "Rejected"] as const).map((statusOption) => (
                    <button
                      key={statusOption}
                      onClick={() => {
                        handleStatusChange(s.id, statusOption);
                        setActiveDropdownId(null);
                      }}
                      className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-gray-100 font-semibold text-gray-700 flex items-center gap-1.5 cursor-pointer"
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        statusOption === "Approved" ? "bg-green-500" : statusOption === "Rejected" ? "bg-red-500" : "bg-yellow-500"
                      }`} />
                      {statusOption}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        );
      },
    },
    {
      label: "CREATED AT",
      render: (s: any) => <span className="text-[13px] text-gray-700">{s.createdAt}</span>,
    },
    {
      label: "ACTIONS",
      render: (s: any) => (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => {
              setSelectedLeave(s);
              setSidebarMode("VIEW");
            }}
            className="p-1.5 border border-[#131327]/10 rounded-full bg-[#F7F6F2] hover:bg-[#e8e7e3] text-gray-600 hover:text-[#212121] transition-colors cursor-pointer"
            title="View Details"
          >
            <IconEye size={13} stroke={2.5} />
          </button>
          <button
            onClick={() => handleOpenEdit(s)}
            className="p-1.5 border border-[#131327]/10 rounded-full bg-[#F7F6F2] hover:bg-[#e8e7e3] text-gray-600 hover:text-[#212121] transition-colors cursor-pointer"
            title="Edit"
          >
            <IconPencil size={13} stroke={2.5} />
          </button>
          <button
            onClick={() => {
              setSelectedLeave(s);
              setSidebarMode("DELETE");
            }}
            className="p-1.5 border border-red-200/55 rounded-full bg-[#FCF8F8] hover:bg-red-50 text-red-500 hover:text-red-700 transition-colors cursor-pointer"
            title="Delete"
          >
            <IconTrash size={13} stroke={2.5} />
          </button>
        </div>
      ),
    },
  ];

  const isSidebarOpen = sidebarMode !== null;

  const activeColumns = isSidebarOpen
    ? columns.filter((_, idx) => [0, 2, 3, 7, 9].includes(idx)) // Fit nicely in smaller width: SR.NO/Checkbox, Name, Period, Status, Actions
    : columns;

  const sidebarContent = (() => {
    switch (sidebarMode) {
      case "ADD":
        return (
          <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 shrink-0 border-b border-gray-100 bg-white">
              <h2 className="text-lg font-bold text-[#212121]">Add New Leave</h2>
              <button
                type="button"
                onClick={() => setSidebarMode(null)}
                className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
                title="Close"
              >
                <IconX size={18} />
              </button>
            </div>
            <AddLeaveModal
              isOpen={sidebarMode === "ADD"}
              onClose={() => setSidebarMode(null)}
              onSave={handleSaveNewLeave}
              isSidebar={true}
            />
          </div>
        );
      case "VIEW":
        return selectedLeave ? (
          <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden p-6 space-y-6">
            <div className="flex items-center justify-between pb-3.5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-[#212121]">Leave Details</h2>
              <button
                type="button"
                onClick={() => {
                  setSidebarMode(null);
                  setSelectedLeave(null);
                }}
                className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
                title="Close"
              >
                <IconX size={18} />
              </button>
            </div>

            <div className="space-y-6">
              {/* User Profile Card */}
              <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex items-center gap-4">
                <img
                  src={selectedLeave.avatar}
                  className="w-12 h-12 rounded-full object-cover border border-gray-200"
                  alt={selectedLeave.name}
                />
                <div className="flex-1">
                  <h3 className="font-bold text-sm text-[#212121]">{selectedLeave.name}</h3>
                  <p className="text-xs text-gray-500">{selectedLeave.dept}</p>
                  <div className="mt-1">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${
                      selectedLeave.status === "Approved"
                        ? "border-green-200 bg-green-50 text-green-700"
                        : selectedLeave.status === "Rejected"
                        ? "border-red-200 bg-red-50 text-red-700"
                        : "border-yellow-200 bg-yellow-50 text-yellow-600"
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        selectedLeave.status === "Approved" ? "bg-green-500" : selectedLeave.status === "Rejected" ? "bg-red-500" : "bg-yellow-500"
                      }`} />
                      {selectedLeave.status === "View Leaves" ? "Pending" : selectedLeave.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Basic Info */}
              <div className="border border-gray-100 rounded-2xl p-5 space-y-4">
                <div className="flex justify-between items-center pb-2.5 border-b border-gray-100/50">
                  <h4 className="text-xs font-bold text-gray-800 uppercase tracking-wider">Basic Info</h4>
                  <span className="text-[10px] font-bold bg-green-50 text-green-700 border border-green-100 px-2.5 py-0.5 rounded-full">
                    {selectedLeave.days} {selectedLeave.days === 1 ? "Full Day" : "Days"}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="block text-[10px] font-bold text-gray-400 uppercase">Type</span>
                    <span className="text-xs font-semibold text-gray-700">{selectedLeave.type}</span>
                  </div>
                  <div>
                    <span className="block text-[10px] font-bold text-gray-400 uppercase">Applied Date</span>
                    <span className="text-xs font-semibold text-gray-700">{selectedLeave.appliedDate}</span>
                  </div>
                </div>

                <div>
                  <span className="block text-[10px] font-bold text-gray-400 uppercase">Leave Period</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs font-semibold text-gray-700">{selectedLeave.leavePeriod}</span>
                    <span className="text-[10px] font-bold bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded">
                      {selectedLeave.days} Day{selectedLeave.days > 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                <div>
                  <span className="block text-[10px] font-bold text-gray-400 uppercase">Absence Reason</span>
                  <span className="text-xs font-medium text-gray-700 block mt-0.5 bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                    {selectedLeave.raw?.reason || "-"}
                  </span>
                </div>

                <div>
                  <span className="block text-[10px] font-bold text-gray-400 uppercase">Files</span>
                  <span className="text-xs text-gray-500 block mt-0.5">No files</span>
                </div>
              </div>

              {/* Actions for Pending Status */}
              {(selectedLeave.status === "Pending" || selectedLeave.status === "View Leaves") && (
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => handleStatusChange(selectedLeave.id, "Approved")}
                    className="flex-1 bg-[#4CAF50] hover:bg-green-600 text-white text-xs font-semibold py-2.5 rounded-xl transition cursor-pointer active:scale-95 flex items-center justify-center gap-1.5"
                  >
                    <IconCheck size={16} /> Approve
                  </button>
                  <button
                    onClick={() => handleStatusChange(selectedLeave.id, "Rejected")}
                    className="flex-1 bg-[#F44336] hover:bg-red-600 text-white text-xs font-semibold py-2.5 rounded-xl transition cursor-pointer active:scale-95 flex items-center justify-center gap-1.5"
                  >
                    <IconX size={16} /> Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : null;
      case "EDIT":
        return selectedLeave ? (
          <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden p-6 space-y-4">
            <div className="flex items-center justify-between pb-3.5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-[#212121]">Edit Leave</h2>
              <button
                type="button"
                onClick={() => {
                  setSidebarMode(null);
                  setSelectedLeave(null);
                }}
                className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
                title="Close"
              >
                <IconX size={18} />
              </button>
            </div>

            {/* Leave Duration Type Radio Buttons */}
            <div className="flex flex-wrap items-center gap-3 bg-slate-50 p-2.5 rounded-xl border border-slate-100">
              {(["Full Day", "Multiple", "First Half", "Second Half"] as const).map((type) => {
                const isSelected = editDurationType === type;
                return (
                  <label
                    key={type}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all cursor-pointer select-none ${
                      isSelected
                        ? "bg-[#FBD037]/15 border-[#FBD037] text-[#212121]"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="editDurationType"
                      checked={isSelected}
                      onChange={() => setEditDurationType(type)}
                      className="hidden"
                    />
                    <span className={`w-2 h-2 rounded-full transition-all ${isSelected ? "bg-[#212121]" : "bg-slate-300"}`} />
                    <span>{type}</span>
                  </label>
                );
              })}
            </div>

            <SectionBlock title="Leave Details" icon={IconFileText}>
              {/* Form Fields */}
              <div className="space-y-4">
                {editDurationType === "Multiple" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <InputField
                      label="From Date *"
                      type="date"
                      value={editFromDate}
                      onChange={(e) => setEditFromDate(e.target.value)}
                    />
                    <InputField
                      label="To Date *"
                      type="date"
                      value={editToDate}
                      onChange={(e) => setEditToDate(e.target.value)}
                    />
                  </div>
                ) : (
                  <InputField
                    label="Select Date *"
                    type="date"
                    value={editSelectedDate}
                    onChange={(e) => setEditSelectedDate(e.target.value)}
                  />
                )}

                <SelectInput
                  label="Employee *"
                  value={editEmployeeId}
                  setValue={setEditEmployeeId}
                  selectOptions={employeesList.map((emp) => ({
                    value: emp.id || emp._id,
                    text: `${emp.firstName} ${emp.lastName}`,
                  }))}
                />

                <div className="grid grid-cols-2 gap-3">
                  <SelectInput
                    label="Leave Type *"
                    value={editLeaveTypeId}
                    setValue={setEditLeaveTypeId}
                    selectOptions={leaveTypesList.map((type) => ({
                      value: type.id || type._id,
                      text: type.leaveType,
                    }))}
                  />
                  <SelectInput
                    label="Status"
                    value={editStatus}
                    setValue={(val) => setEditStatus(val as any)}
                    selectOptions={[
                      { value: "PENDING", text: "Pending" },
                      { value: "APPROVED", text: "Approved" },
                      { value: "REJECTED", text: "Rejected" },
                    ]}
                  />
                </div>

                <TextareaField
                  label="Reason For Absence *"
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  rows={3}
                  placeholder="Reason"
                />

                {/* Documents Box */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1">Documents</label>
                  <div className="border border-dashed border-gray-300 rounded-xl p-4 flex flex-col items-center justify-center bg-slate-50/50 hover:bg-slate-50 cursor-pointer transition">
                    <IconUpload size={20} className="text-gray-400 mb-1" />
                    <span className="text-xs font-bold text-[#212121]">Drag & drop or click to browse</span>
                    <span className="text-[10px] text-gray-400 mt-0.5">Maximum 10 files. Max 3MB per file</span>
                  </div>
                </div>
              </div>
            </SectionBlock>

            {/* Footer Buttons */}
            <div className="flex justify-end gap-2.5 pt-3 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setSidebarMode(null);
                  setSelectedLeave(null);
                }}
                className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={isSubmittingEdit}
                className="px-4 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] text-xs font-bold rounded-xl transition cursor-pointer border-none shadow-sm disabled:opacity-60 active:scale-95"
              >
                {isSubmittingEdit ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        ) : null;
      case "VIEW_LEAVES":
        return selectedLeave ? (
          <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden p-6 space-y-6">
            <div className="flex items-center justify-between pb-3.5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-[#212121]">Leaves ({selectedLeave.name})</h2>
              <button
                type="button"
                onClick={() => {
                  setSidebarMode(null);
                  setSelectedLeave(null);
                }}
                className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
                title="Close"
              >
                <IconX size={18} />
              </button>
            </div>

            {/* Content Table/List */}
            <div className="overflow-y-auto max-h-[calc(100vh-220px)] space-y-3 pr-1">
              {(selectedLeave.leaveDays || []).map((day: any) => {
                const isDayApproved = day.status === "Approved";
                const isDayRejected = day.status === "Rejected";
                const isDayCancelled = day.status === "Cancelled";

                return (
                  <div key={day.id} className="p-4 border border-gray-100 rounded-2xl bg-slate-50/50 hover:bg-slate-50 transition-colors flex items-center justify-between gap-4">
                    <div className="space-y-1 text-left">
                      <div className="text-xs font-bold text-[#212121]">{day.date}</div>
                      <div className="text-[10px] text-gray-500 font-semibold">{day.type || selectedLeave.type}</div>
                      <div className="text-[9px] text-gray-400">Applied: {day.appliedDate || selectedLeave.appliedDate}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-[10px] font-bold transition shadow-sm ${
                        isDayApproved
                          ? "border-green-200 bg-green-50 text-green-700"
                          : isDayRejected || isDayCancelled
                          ? "border-red-200 bg-red-50 text-red-600"
                          : "border-yellow-200 bg-yellow-50 text-yellow-600"
                      }`}>
                        {day.status}
                      </span>

                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDialogDropdownId(dialogDropdownId === day.id ? null : day.id);
                          }}
                          className="p-1 rounded-md hover:bg-gray-200 text-slate-500 transition cursor-pointer"
                        >
                          <IconDots size={16} />
                        </button>

                        {dialogDropdownId === day.id && (
                          <>
                            <div className="fixed inset-0 z-20" onClick={() => setDialogDropdownId(null)} />
                            <div className="absolute right-0 mt-1 w-24 bg-white rounded-lg border border-gray-200 shadow-lg py-1 z-30 animate-in fade-in duration-100">
                              <button
                                onClick={() => {
                                  setDialogDropdownId(null);
                                  handleOpenDayActionInSidebar(day, "VIEW");
                                }}
                                className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-gray-50 font-semibold text-gray-700 block cursor-pointer border-none"
                              >
                                View
                              </button>
                              <button
                                onClick={() => {
                                  setDialogDropdownId(null);
                                  handleOpenDayActionInSidebar(day, "EDIT");
                                }}
                                className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-gray-50 font-semibold text-gray-700 block cursor-pointer border-none"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  setDialogDropdownId(null);
                                  handleOpenDayActionInSidebar(day, "DELETE");
                                }}
                                className="w-full text-left px-3 py-1.5 text-[11px] hover:bg-red-50 font-semibold text-red-600 block cursor-pointer border-none"
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 pt-3.5 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setSidebarMode(null);
                  setSelectedLeave(null);
                }}
                className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
              >
                Close
              </button>
            </div>
          </div>
        ) : null;
      case "DELETE":
        return selectedLeave ? (
          <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden p-6 space-y-6">
            <div className="flex items-center justify-between pb-3.5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-[#212121]">Delete Leave</h2>
              <button
                type="button"
                onClick={() => {
                  setSidebarMode(null);
                  setSelectedLeave(null);
                }}
                className="p-1.5 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
                title="Close"
              >
                <IconX size={18} />
              </button>
            </div>

            <p className="text-sm text-gray-600 font-medium">
              Are you sure you want to delete this leave?
            </p>

            <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl flex items-center gap-4">
              <img
                src={selectedLeave.avatar}
                className="w-10 h-10 rounded-full object-cover border border-gray-200"
                alt={selectedLeave.name}
              />
              <div className="flex-1">
                <h3 className="font-bold text-xs text-[#212121]">{selectedLeave.name}</h3>
                <p className="text-[10px] text-gray-400">{selectedLeave.leavePeriod}</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => {
                  setSidebarMode(null);
                  setSelectedLeave(null);
                }}
                className="px-4 py-2 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteLeave}
                className="px-4 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] text-xs font-bold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm hover:shadow-md duration-150 hover:-translate-y-0.5 active:translate-y-0"
              >
                Delete
              </button>
            </div>
          </div>
        ) : null;
      default:
        return null;
    }
  })();

  return (
    <div className="grid grid-cols-12 gap-6 p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 rounded-r-3xl rounded-bl-3xl w-full mt-2 animate-in fade-in duration-300">
      <div className={isSidebarOpen ? "col-span-12 xl:col-span-7" : "col-span-12"}>
        <div className="bg-white rounded-3xl border border-gray-200/50 shadow-sm overflow-hidden">
          {/* Toolbar Controls */}
          <div className="px-6 py-1.5 flex flex-col lg:flex-row lg:items-center justify-end gap-4 border-b border-slate-100">
            <div className="flex items-center flex-wrap gap-2.5">
              {/* Filter Icon Button */}
              <div className="relative group">
                <button className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer">
                  <IconFilter size={17} stroke={2} />
                </button>
              </div>

              {/* Search Sliding Input Box */}
              <div className="relative flex items-center group">
                <div
                  onClick={() => {
                    if (!showSearch) {
                      setShowSearch(true);
                    }
                  }}
                  className={`flex items-center transition-all duration-300 ease-in-out border border-slate-200 rounded-lg bg-white shadow-sm h-8 ${
                    showSearch
                      ? "w-48 px-2 cursor-default"
                      : "w-8 justify-center cursor-pointer hover:bg-slate-50 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95"
                  }`}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSearch(!showSearch);
                    }}
                    className="flex items-center justify-center text-slate-700 focus:outline-none flex-shrink-0"
                  >
                    <IconSearch size={17} stroke={2.5} />
                  </button>
                  {showSearch && (
                    <>
                      <input
                        type="text"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                        className="flex-1 ml-2 bg-transparent text-xs text-slate-800 placeholder-slate-400 outline-none border-none animate-in fade-in duration-200"
                      />
                      {searchQuery && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchQuery("");
                          }}
                          className="flex items-center justify-center p-0.5 ml-1 text-slate-400 hover:text-slate-600 focus:outline-none flex-shrink-0"
                        >
                          <IconX size={14} stroke={2.5} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Refresh Button */}
              <div className="relative group">
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setEntriesLimit(10);
                    setShowSearch(false);
                  }}
                  className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer"
                  title="Reset"
                >
                  <IconRefresh size={17} stroke={2.5} />
                </button>
              </div>

              {/* Import Button */}
              <div className="relative group">
                <button className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer" title="Import">
                  <IconUpload size={17} stroke={2.5} />
                </button>
              </div>

              {/* Export Button */}
              <div className="relative group">
                <button className="w-8 h-8 flex items-center justify-center bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition-all duration-150 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:scale-95 cursor-pointer" title="Export">
                  <IconDownload size={17} stroke={2.5} />
                </button>
              </div>

              {/* Entries Selector component */}
              <EntriesSelector
                value={entriesLimit}
                options={[10, 20, 50]}
                onChange={(val) => setEntriesLimit(val)}
              />

              {/* Add button inside toolbar */}
              <button
                onClick={() => setSidebarMode("ADD")}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#FACC15] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer"
                title="Add Leave"
              >
                <IconPlus stroke={2.5} size={16} className="text-[#212121]" />
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="p-8 flex justify-center items-center">
              <svg
                className="animate-spin h-6 w-6 text-[#FBD037]"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                />
              </svg>
            </div>
          ) : error ? (
            <div className="p-8 text-center text-red-500 font-semibold">{error}</div>
          ) : (
            <ListView
              columns={activeColumns}
              data={displayedLeaves}
              trClassName="hover:bg-[#FBD037]/10 group transition-colors duration-150 border-b border-gray-100 cursor-pointer"
              mainDivClassName="bg-[#F7F6F2] rounded-3xl"
              mailDivClassName="custom-scrollbar bg-[#FCFDF8] rounded-3xl shadow-inner max-h-[75vh]"
              scrollbarClassName="custom-scrollbar"
              thClassName="text-[11px] font-semibold tracking-wider uppercase select-none text-[#131327]/70"
            />
          )}

          {/* Pagination Footer */}
          <div className="py-4 flex justify-center">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm cursor-pointer"
                disabled={currentPage === 1 || isLoading}
              >
                <IconChevronLeft size={14} stroke={2.5} />
              </button>
              <button className="w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold bg-[#FBD037] border-[#FBD037] text-[#131327] transition shadow-sm">
                {currentPage}
              </button>
              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm cursor-pointer"
                disabled={leavesData.length < entriesLimit || isLoading}
              >
                <IconChevronRight size={14} stroke={2.5} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Sidebar Right Panel ────────────────────────────────────────── */}
      {isSidebarOpen && (
        <>
          {/* Backdrop on mobile */}
          <div
            className="fixed inset-0 bg-black/40 z-30 xl:hidden"
            onClick={() => {
              setSidebarMode(null);
              setSelectedLeave(null);
            }}
          />
          <div className="col-span-12 xl:col-span-5 space-y-4 mt-2 xl:static fixed inset-y-0 right-0 z-[35] w-full sm:w-[450px] xl:w-auto p-4 pt-[76px] sm:pt-[96px] xl:p-0 shadow-2xl xl:shadow-none bg-white xl:bg-transparent overflow-y-auto xl:overflow-visible flex flex-col justify-start">
            {sidebarContent}
          </div>
        </>
      )}

      {/* Reject Leave Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4 overflow-y-auto backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl p-6 w-full max-w-md border border-gray-100 shadow-2xl relative flex flex-col gap-5 animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2 text-red-600">
                <IconAlertTriangle size={22} className="stroke-[2.5]" />
                <h3 className="text-base font-bold text-gray-900">Reject Leave</h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsRejectModalOpen(false);
                  setRejectionLeaveId(null);
                  setRejectionReason("");
                }}
                className="p-1 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer"
                title="Close"
              >
                <IconX size={18} />
              </button>
            </div>

            {/* Description */}
            <p className="text-xs text-gray-500 font-medium leading-relaxed">
              Please provide a reason for rejection. This field is mandatory.
            </p>

            {/* Reason Field */}
            <div className="space-y-1.5 text-left">
              <label className="block text-xs font-bold text-gray-800">
                Reason for Rejection <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter the reason for rejection..."
                rows={4}
                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl outline-none focus:ring-1 focus:ring-[#FBD037] text-xs text-[#212121] resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setIsRejectModalOpen(false);
                  setRejectionLeaveId(null);
                  setRejectionReason("");
                }}
                className="px-5 py-2.5 border border-gray-200 hover:bg-gray-50 text-xs font-semibold rounded-xl text-slate-600 transition cursor-pointer bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRejectSubmit}
                disabled={isRejectSubmitting}
                className="px-5 py-2.5 bg-black hover:bg-slate-900 text-white text-xs font-bold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm"
              >
                {isRejectSubmitting ? (
                  <svg className="animate-spin h-4 w-4 text-white mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                ) : null}
                Submit Rejection
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}




























